import { useCallback, useEffect, useRef, useState } from 'react'
import type { Dispatch, RefObject, SetStateAction } from 'react'

export interface UseDismissablePopoverResult<C extends HTMLElement, T extends HTMLElement> {
  /** Whether the popover is currently open. */
  open: boolean
  /** Low-level setter (rarely needed; prefer `toggle` / `close`). */
  setOpen: Dispatch<SetStateAction<boolean>>
  /** Flip the open state — wire to the trigger's onClick. */
  toggle: () => void
  /**
   * Close the popover. Pass `true` to return focus to the trigger (used for
   * keyboard dismissal or when focused popover content is removed).
   */
  close: (restoreFocus?: boolean) => void
  /** Attach to the popover's outer wrapper — outside-clicks here are ignored. */
  containerRef: RefObject<C | null>
  /** Attach to the trigger button — receives focus on Escape dismissal. */
  triggerRef: RefObject<T | null>
}

export interface UseDismissablePopoverOptions {
  /** A portaled panel that should also count as inside the popover. */
  additionalContainerRef?: RefObject<HTMLElement | null>
  /** Restore focus without scrolling the trigger into view. Defaults to `false`. */
  preventScrollOnFocusRestore?: boolean
}

/**
 * Open/close state + dismissal behaviour for a trigger-anchored popover menu
 * (account menu, dropdown, …): outside-click (mousedown) closes it, Escape
 * closes it and restores focus to the trigger. Extracted from the near-identical
 * effect duplicated in the web and desktop AccountMenu so both share one source
 * of truth. Purely behavioural — no markup or styling — hence it lives here in
 * @kiao-voice/hooks rather than ui-kit.
 *
 * Standardises on `mousedown` for the outside-close (fires before the click,
 * so a click that lands outside dismisses before any underlying handler runs).
 */
export function useDismissablePopover<
  C extends HTMLElement = HTMLDivElement,
  T extends HTMLElement = HTMLButtonElement,
>({
  additionalContainerRef,
  preventScrollOnFocusRestore = false,
}: UseDismissablePopoverOptions = {}): UseDismissablePopoverResult<C, T> {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<C | null>(null)
  const triggerRef = useRef<T | null>(null)

  const toggle = useCallback(() => setOpen((value) => !value), [])

  const close = useCallback(
    (restoreFocus = false) => {
      if (restoreFocus) {
        if (preventScrollOnFocusRestore) {
          triggerRef.current?.focus({ preventScroll: true })
        } else {
          triggerRef.current?.focus()
        }
      }
      setOpen(false)
    },
    [preventScrollOnFocusRestore]
  )

  useEffect(() => {
    if (!open) {
      return
    }

    const handlePointerDown = (event: MouseEvent) => {
      const container = containerRef.current
      const additionalContainer = additionalContainerRef?.current
      if (!container && !additionalContainer) {
        return
      }

      const target = event.target as Node
      const isInsideContainer = container?.contains(target) ?? false
      const isInsideAdditionalContainer = additionalContainer?.contains(target) ?? false
      if (!isInsideContainer && !isInsideAdditionalContainer) {
        const activeElement = document.activeElement
        const focusIsInside =
          (activeElement && container?.contains(activeElement)) ||
          (activeElement && additionalContainer?.contains(activeElement)) ||
          false
        close(focusIsInside)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        close(true)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, close, additionalContainerRef])

  return { open, setOpen, toggle, close, containerRef, triggerRef }
}
